
import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { X } from 'lucide-react';

interface TranscriptSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentTitle: string;
  transcript: string;
}

const TranscriptSheet = ({ open, onOpenChange, assessmentTitle, transcript }: TranscriptSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md md:max-w-lg">
        <SheetHeader className="mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <SheetTitle className="text-xl">Chat Transcript</SheetTitle>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-full p-1 hover:bg-muted"
          >
            <X size={18} />
          </button>
        </SheetHeader>
        
        <div className="space-y-6 overflow-y-auto max-h-[80vh]">
          {/* Sample transcript conversation based on the image */}
          <div className="space-y-6">
            {/* Bot message - left side */}
            <div className="flex items-start">
              <div className="max-w-[75%] flex items-start space-x-2">
                <Avatar className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm">
                  <AvatarFallback>RO</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center mb-1">
                    <p className="font-medium">RO Bot</p>
                    <p className="text-xs text-muted-foreground ml-2">01:01 PM</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg rounded-tl-none">
                    <p className="text-sm">
                      Let's start with your most recent or most relevant experience as a {assessmentTitle}. Could you describe that experience and highlight any key projects, technologies, and outcomes?
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* User response - right side */}
            <div className="flex items-start justify-end">
              <div className="max-w-[75%] flex flex-col items-end">
                <div className="flex items-center justify-end mb-1">
                  <p className="text-xs text-muted-foreground mr-2">01:02 PM</p>
                  <p className="font-medium">Candidate</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="flex-1">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg rounded-tr-none">
                      <p className="text-sm">
                        {transcript.split('.')[0]}. {transcript.split('.')[1] || ''}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          Technical Recruitment
                        </span>
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                          Leadership
                        </span>
                      </div>
                    </div>
                  </div>
                  <Avatar className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm">
                    <AvatarFallback>RT</AvatarFallback>
                  </Avatar>
                </div>
              </div>
            </div>
            
            {/* More conversation - left side */}
            <div className="flex items-start">
              <div className="max-w-[75%] flex items-start space-x-2">
                <Avatar className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm">
                  <AvatarFallback>RO</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center mb-1">
                    <p className="font-medium">RO Bot</p>
                    <p className="text-xs text-muted-foreground ml-2">01:03 PM</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg rounded-tl-none">
                    <p className="text-sm">
                      Can you describe a specific challenging hire you managed and how you overcame any obstacles?
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Final user response - right side */}
            <div className="flex items-start justify-end">
              <div className="max-w-[75%] flex flex-col items-end">
                <div className="flex items-center justify-end mb-1">
                  <p className="text-xs text-muted-foreground mr-2">01:04 PM</p>
                  <p className="font-medium">Candidate</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="flex-1">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg rounded-tr-none">
                      <p className="text-sm">
                        {transcript.split('.').slice(2, 4).join('.')}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          .NET
                        </span>
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          Azure
                        </span>
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                          Sourcing
                        </span>
                      </div>
                    </div>
                  </div>
                  <Avatar className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm">
                    <AvatarFallback>RT</AvatarFallback>
                  </Avatar>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TranscriptSheet;
